/**
 * 一覧・詳細の 3 種のフォールバック表示（空 / 失敗 / 未存在）。
 *
 * `frontend.md`「深いリンクから到達した URL は 404 を出さない」に対応する。3 状態を
 * 別々の部品に分けるのは、利用者が次に取れる行動が違うためである。空は条件を変える、
 * 失敗は時間をおいて再読み込みする、未存在は一覧へ戻る。1 つの汎用「データがありません」
 * に畳むと、削除済みなのか通信失敗なのかが利用者に区別できない。
 *
 * 復帰導線は `action` スロットで受け取る。リンク先はルート定義を持つ画面側にしか
 * 決められないので、共有部品側でルートを知らない形にしておく。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 空・失敗・未存在の 3 状態を、次の行動が分かる形で描き分ける。
 * @graph-connects none
 */
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** フォールバック 3 種に共通の骨格の props。 @graph-connects none */
interface FallbackShellProps {
  label: string;
  title: string;
  action?: ReactNode;
  tone: 'muted' | 'error';
  /** 画面には出さず、調べる人のために `data-id` へ残す識別子。 */
  dataId?: string;
}

/**
 * 3 種のフォールバックが共有する枠。ラベル・見出し・復帰導線の並びを 1 つに保つ。
 *
 * 補足のスロットは持たせない。3 種とも「利用者に伝えたい 1 文」が `title` に収まるため、
 * 呼び出し元が 1 つも無いスロットを枠側に残すと、到達しない分岐だけが増える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フォールバック表示の枠（ラベル・見出し・復帰導線）を共通化する。
 * @graph-connects none
 */
function FallbackShell({ label, title, action, tone, dataId }: FallbackShellProps) {
  return (
    <Card padding="lg" role="status" data-id={dataId}>
      <div
        className={
          tone === 'error'
            ? 'font-mono text-xs tracking-widest text-[var(--color-error)] uppercase'
            : 'font-mono text-xs tracking-widest text-[var(--color-text-muted)] uppercase'
        }
      >
        {label}
      </div>
      <p className="mt-3 text-lg font-bold text-[var(--color-text-primary)]">{title}</p>
      {action !== undefined && <div className="mt-4">{action}</div>}
    </Card>
  );
}

/** `EmptyState` の props。 */
export interface EmptyStateProps {
  /** 「なぜ空か」を条件の言葉で書いたメッセージ。 */
  message: string;
  /** 条件をやり直す導線などの任意スロット。 */
  action?: ReactNode;
}

/**
 * 条件に一致するものが無い状態。データ取得は成功している。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得は成功したが該当が 0 件である状態を描く。
 * @graph-connects none
 */
export function EmptyState({ message, action }: EmptyStateProps) {
  const { t } = useTranslation();

  return (
    <FallbackShell tone="muted" label={t('spec.emptyTitle')} title={message} action={action} />
  );
}

/** `ErrorState` の props。 */
export interface ErrorStateProps {
  /** 取得エラー。`Error` ならメッセージを出す。 */
  error: unknown;
  /** 再試行などの任意スロット。 */
  action?: ReactNode;
}

/**
 * 取得そのものが失敗した状態。原因の文言を出して、未存在と区別できるようにする。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得失敗を原因つきで描き、未存在・空と区別できるようにする。
 * @graph-connects none
 */
export function ErrorState({ error, action }: ErrorStateProps) {
  const { t } = useTranslation();
  const message = error instanceof Error ? error.message : t('spec.errorDescription');

  return (
    <FallbackShell tone="error" label={t('spec.errorTitle')} title={message} action={action} />
  );
}

/** `NotFoundState` の props。 */
export interface NotFoundStateProps {
  /** 見つからなかったリソースの ID。 */
  id: string;
  /** 一覧へ戻る導線。深いリンクからの到達で必ず渡す。 */
  action: ReactNode;
}

/**
 * 深いリンクで指された ID が存在しない状態。一覧への復帰導線を必須の props にして、
 * 「戻れない行き止まり」を型で作れなくする。
 *
 * **ID は本文に出さない**（`data-id` にだけ残す）。存在しない要素には名前が無く、番号だけを
 * 見せても人はそれが何だったかを判別できない。読み手に要るのは「開いた先が無い」ことと
 * 戻る導線である。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 深いリンク到達時の未存在を、番号を出さずに一覧への復帰導線つきで描く。
 * @graph-connects none
 */
export function NotFoundState({ id, action }: NotFoundStateProps) {
  const { t } = useTranslation();

  return (
    <FallbackShell
      tone="muted"
      label={t('spec.notFoundTitle')}
      title={t('spec.notFoundDescription')}
      action={action}
      dataId={id}
    />
  );
}
